/**
 * Pure helper functions extracted from PromptContext.jsx.
 * No React dependencies, no project imports — consumers provide all data.
 */

/**
 * Build messages from a prompt definition object, replacing {{varName}} placeholders.
 *
 * @param {Object} prompt - A prompt definition with systemPrompt, userPromptTemplate, model, etc.
 * @param {Object} [variables={}] - Key/value pairs to substitute into the template.
 *   Object values are JSON.stringify'd with 2-space indent; everything else is String()'d.
 * @returns {{ messages: Array, model: string, maxTokens: number, temperature: number, outputFormat: string }}
 * @throws {Error} If prompt is falsy.
 */
export function buildMessagesFromPrompt(prompt, variables = {}) {
  if (!prompt) {
    throw new Error('Prompt is required');
  }

  let userContent = prompt.userPromptTemplate;
  Object.entries(variables).forEach(([key, value]) => {
    const stringValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
    userContent = userContent.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), stringValue);
  });

  return {
    messages: [
      { role: 'system', content: prompt.systemPrompt },
      { role: 'user', content: userContent },
    ],
    model: prompt.model,
    maxTokens: prompt.maxTokens,
    temperature: prompt.temperature,
    outputFormat: prompt.outputFormat,
  };
}

/**
 * Merge fallback prompt definitions with Firestore prompt docs.
 * Firestore docs override fallbacks for matching IDs, but only if their status
 * is 'active' or 'testing'. All fallbacks are included regardless.
 *
 * @param {Object} fallbacks - Map of promptId -> prompt definition (always included).
 * @param {Array}  firestoreDocs - Array of Firestore prompt docs, each with at least { id, status, ...fields }.
 * @returns {Object} Merged map of promptId -> prompt definition.
 */
export function mergePrompts(fallbacks, firestoreDocs) {
  const firestorePrompts = {};
  (firestoreDocs || []).forEach(doc => {
    if (doc.status === 'active' || doc.status === 'testing') {
      firestorePrompts[doc.id] = { ...doc };
    }
  });

  return { ...fallbacks, ...firestorePrompts };
}
